import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import StripeCheckout from '../../Components/StripeCheckout';
import './index.scss';
import { formatPrice } from '../../Utils/formatting';
import { tLoader } from '@/Utils/i18n';
import { languageManager } from '@/Utils/languageManager';

// Stripe publishable keys
const test_PK = 'pk_test_51RMJLQEHF2kyRI5HVC7XZHF9pCHGavOQo3e1EvgYA6Rhs8bNs10cAqdnhwFLZ8JgvBc3WuOAYlyNHeSLbysCXt1M00Ov1Ziv5i';
const real_PK = 'pk_live_51RMJLQEHF2kyRI5HOvQxhGSrZPtxPzoQKYIXXMaYFSY3JuJJ5Q4bJRwKrWJ5Ww2C9NTHVeMucSPe7CxDbc9dqDCS00LZME45Uw';

// Initialize Stripe with publishable key
const stripePromise = loadStripe(process.env.NODE_ENV === 'production' ? real_PK : test_PK);

const translations = {
    en: {
        "Loading payment information...": "Loading payment information...",
        "Return to Product": "Return to Product",
        "Confirm Your Purchase": "Confirm Your Purchase",
        "Teacher:": "Teacher:",
        "Session Time:": "Session Time:",
        "Price:": "Price:",
        "Payment information not found. Please return to the product page.": "Payment information not found. Please return to the product page.",
        "An error occurred while retrieving payment information.": "An error occurred while retrieving payment information."
    },
    id: {
        "Loading payment information...": "Memuat informasi pembayaran...",
        "Return to Product": "Kembali ke Produk",
        "Confirm Your Purchase": "Konfirmasi Pembelian Anda",
        "Teacher:": "Guru:",
        "Session Time:": "Waktu Sesi:",
        "Price:": "Harga:",
        "Payment information not found. Please return to the product page.": "Informasi pembayaran tidak ditemukan. Silakan kembali ke halaman produk.",
        "An error occurred while retrieving payment information.": "Terjadi kesalahan saat mengambil informasi pembayaran."
    },
    ko: {
        "Loading payment information...": "결제 정보를 불러오는 중...",
        "Return to Product": "제품으로 돌아가기",
        "Confirm Your Purchase": "구매 확인",
        "Teacher:": "선생님:",
        "Session Time:": "세션 시간:",
        "Price:": "가격:",
        "Payment information not found. Please return to the product page.": "결제 정보를 찾을 수 없습니다. 제품 페이지로 돌아가 주세요.",
        "An error occurred while retrieving payment information.": "결제 정보를 가져오는 중 오류가 발생했습니다."
    },
    vn: {
        "Loading payment information...": "Đang tải thông tin thanh toán...",
        "Return to Product": "Quay lại Sản phẩm",
        "Confirm Your Purchase": "Xác nhận Mua hàng",
        "Teacher:": "Giáo viên:",
        "Session Time:": "Thời gian Buổi học:",
        "Price:": "Giá:",
        "Payment information not found. Please return to the product page.": "Không tìm thấy thông tin thanh toán. Vui lòng quay lại trang sản phẩm.",
        "An error occurred while retrieving payment information.": "Đã xảy ra lỗi khi lấy thông tin thanh toán."
    }
};

interface PaymentPageProps {
    clientSecret: string | null;
}

interface PaymentInfo {
    productId: string;
    productName: string;
    productPrice: number;
    currency?: string;
    sessionDate?: string;
    sessionTime?: string;
    teacherName?: string;
}

const PaymentPage: React.FC<PaymentPageProps> = ({
    clientSecret
}) => {
    const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [, forceUpdate] = useState({});
    const t = tLoader(translations);

    // Parse dates from ISO strings
    const sessionDate = paymentInfo?.sessionDate ? new Date(paymentInfo.sessionDate) : undefined;
    const sessionTime = paymentInfo?.sessionTime ? new Date(paymentInfo.sessionTime) : undefined;

    useEffect(() => {
        const unsubscribe = languageManager.subscribe(() => forceUpdate({}));
        return unsubscribe;
    }, []);
    
    useEffect(() => {
        try {
            // Get the payment info from sessionStorage
            const storedData = sessionStorage.getItem('payment_info');
            if (storedData) {
                const parsedData = JSON.parse(storedData);
                setPaymentInfo(parsedData);
            } else {
                setError(t('Payment information not found. Please return to the product page.'));
            }
        } catch (err) {
            console.error('Error parsing payment data:', err);
            setError(t('An error occurred while retrieving payment information.'));
        } finally {
            setLoading(false);
        }
    }, [t]);
    
    const handlePaymentSuccess = () => {
        // Clear the payment info from sessionStorage
        sessionStorage.removeItem('payment_info');
        // Wait for the success modal to be dismissed before redirecting
        setTimeout(() => {
            window.location.href = '/';
        }, 1000);
    };
    
    const handlePaymentCancel = () => {
        // Go back to the product page
        window.history.back();
    };
    return (
        <div className="payment-page">
            <header className="payment-header">
                <div className="logo-container">
                    <img src="/logo-white.svg" alt="Korean Mate Logo" className="header-logo" />
                </div>
            </header>

            <div className="payment-container">
                {loading ? (
                    <div className="loading-container">
                        <div className="loading-spinner"></div>
                        <p>{t("Loading payment information...")}</p>
                    </div>
                ) : error ? (
                    <div className="error-container">
                        <p className="error-message">{error}</p>
                        <button onClick={() => window.history.back()} className="back-button">{t("Return to Product")}</button>
                    </div>
                ) : paymentInfo && (
                    <>
                        <div className="payment-summary">
                            <h1>{t("Confirm Your Purchase")}</h1>

                            <div className="product-details">
                                <div className="product-image">
                                    <img src="/feature1.png" alt={paymentInfo.productName} />
                                </div>

                                <div className="summary-content">
                                    <h2>{paymentInfo.productName}</h2>

                                    {paymentInfo.teacherName && (
                                        <div className="summary-item">
                                            <span className="label">{t("Teacher:")}</span>
                                            <span className="value">{paymentInfo.teacherName}</span>
                                        </div>
                                    )}

                                    {sessionDate && sessionTime && (
                                        <div className="summary-item">
                                            <span className="label">{t("Session Time:")}</span>
                                            <span className="value">
                                                {sessionDate.toLocaleDateString()} at{' '}
                                                {sessionTime.getHours()}:00
                                            </span>
                                        </div>
                                    )}

                                    <div className="summary-item price">
                                        <span className="label">{t("Price:")}</span>
                                        <span className="value">{formatPrice(paymentInfo.productPrice, paymentInfo.currency || 'USD')}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <div className="payment-form-container">
                            <Elements stripe={stripePromise}>
                                <StripeCheckout
                                    price={paymentInfo.productPrice}
                                    productName={paymentInfo.productName}
                                    clientSecret={clientSecret}
                                    onSuccess={handlePaymentSuccess}
                                    onCancel={handlePaymentCancel}
                                />
                            </Elements>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default PaymentPage;